import ResponsiveSidebar from "../../General/ResponsiveSidebar/ResponsiveSidebar";
import React from "react";
import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  LocalShipping as LocalShippingIcon,
  Group as GroupIcon,
  ExpandLess,
  ExpandMore,
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  Build as BuildIcon,
  CalendarMonth as CalendarMonthIcon,
  Assessment as AssessmentIcon,
  Inventory2 as InventoryIcon,
} from "@mui/icons-material";
import { Link as RouterLink, useLocation } from "react-router-dom";
import useAuth from "../../../hooks/useAuth";

const drawerWidth = 240;
const collapsedWidth = 60;

interface FleetSidebarProps {
  open?: boolean;
  toggle?: () => void;
}

const FleetSidebar = ({
  open = false,
  toggle = () => {},
}: FleetSidebarProps) => {
  const location = useLocation();
  const [repairsOpen, setRepairsOpen] = React.useState(false);
  const [repairsAnchorEl, setRepairsAnchorEl] =
    React.useState<null | HTMLElement>(null);

  const { role: roles } = useAuth();
  const hasRole = React.useCallback(
    (r: string) => (roles ? roles.includes(r) : false),
    [roles],
  );

  const fleetMenuItems = [
    { text: "Dashboard", icon: <DashboardIcon />, path: "/fleet" },
    { text: "Assets", icon: <LocalShippingIcon />, path: "/fleet/assets" },
    { text: "Drivers", icon: <GroupIcon />, path: "/fleet/drivers" },
    {
      text: "Equipment",
      icon: <InventoryIcon />,
      path: "/fleet/equipment-inventory",
    },
    { text: "Dispatchers", icon: <GroupIcon />, path: "/fleet/dispatchers" },
    {
      text: "Yard Recall Schedule",
      icon: <CalendarMonthIcon />,
      path: "/fleet/yardRecallSchedules",
    },
    { text: "Reports", icon: <AssessmentIcon />, path: "/fleet/reports" },
  ];

  const repairsItems = [
    { text: "OTR Repairs", path: "/fleet/incidents" },
    { text: "Yard Repairs", path: "/fleet/yardRepairs" },
    { text: "Repair Shops", path: "/fleet/repairShops" },
  ];

  const isSelected = (path: string) => location.pathname.startsWith(path);
  const repairsSelected = repairsItems.some((item) => isSelected(item.path));

  const handleRepairsMenuOpen = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => setRepairsAnchorEl(event.currentTarget);
  const handleRepairsMenuClose = () => setRepairsAnchorEl(null);

  return (
    <>
      <ResponsiveSidebar
        open={open}
        onClose={toggle}
        sx={(theme) => ({
          flexShrink: 0,
          width: 0,
          "& .MuiDrawer-paper": {
            width: open ? drawerWidth : collapsedWidth,
            boxSizing: "border-box",
            top: 64,
            height: "calc(100% - 64px)",
            overflowX: "hidden",
            position: "fixed",
            transition: theme.transitions.create("width", {
              duration: theme.transitions.duration.standard,
            }),
          },
        })}
      >
        <Toolbar sx={{ justifyContent: open ? "flex-end" : "center" }}>
          <IconButton
            onClick={toggle}
            aria-label={
              open ? "Close section navigation" : "Open section navigation"
            }
          >
            {open ? <ChevronLeftIcon /> : <MenuIcon />}
          </IconButton>
        </Toolbar>
        <List>
          {hasRole("Fleet") &&
            fleetMenuItems.map((item) => {
              const button = (
                <ListItemButton
                  key={item.text}
                  selected={isSelected(item.path)}
                  component={RouterLink}
                  to={item.path}
                  sx={{ justifyContent: open ? "initial" : "center" }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : 0,
                      justifyContent: "center",
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    sx={{ opacity: open ? 1 : 0 }}
                  />
                </ListItemButton>
              );
              return open ? (
                button
              ) : (
                <Tooltip key={item.text} title={item.text} placement="right">
                  {button}
                </Tooltip>
              );
            })}

          {hasRole("Fleet") && (
            <>
              {open ? (
                <ListItemButton
                  selected={repairsSelected}
                  onClick={
                    open
                      ? () => setRepairsOpen(!repairsOpen)
                      : handleRepairsMenuOpen
                  }
                  sx={{ justifyContent: open ? "initial" : "center" }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : 0,
                      justifyContent: "center",
                    }}
                  >
                    <BuildIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Repairs"
                    sx={{ opacity: open ? 1 : 0 }}
                  />
                  {open && (repairsOpen ? <ExpandLess /> : <ExpandMore />)}
                </ListItemButton>
              ) : (
                <Tooltip title="Repairs" placement="right">
                  <ListItemButton
                    selected={repairsSelected}
                    onClick={handleRepairsMenuOpen}
                    sx={{ justifyContent: "center" }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: 0,
                        justifyContent: "center",
                      }}
                    >
                      <BuildIcon />
                    </ListItemIcon>
                  </ListItemButton>
                </Tooltip>
              )}
              {open ? (
                <Collapse in={repairsOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {repairsItems.map((item) => (
                      <ListItemButton
                        key={item.text}
                        sx={{ pl: 4 }}
                        selected={isSelected(item.path)}
                        component={RouterLink}
                        to={item.path}
                      >
                        <ListItemText primary={item.text} />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              ) : (
                <Menu
                  anchorEl={repairsAnchorEl}
                  open={Boolean(repairsAnchorEl)}
                  onClose={handleRepairsMenuClose}
                  anchorOrigin={{ vertical: "top", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "left" }}
                >
                  {repairsItems.map((item) => (
                    <MenuItem
                      key={item.text}
                      component={RouterLink}
                      to={item.path}
                      onClick={handleRepairsMenuClose}
                    >
                      {item.text}
                    </MenuItem>
                  ))}
                </Menu>
              )}
            </>
          )}
        </List>
      </ResponsiveSidebar>
    </>
  );
};

export default FleetSidebar;
